import { type ReactNode } from 'react';
import { type MeVoucher } from '@varga/shared';
import { dataLonga } from '../lib/formato';
import { IconePresente } from './icones';

/**
 * `CardVoucher` — o canhoto (DESIGN-FOUNDATION §5.3 / §6.4). O voucher é ao
 * portador, transferível e **não expira**: funcionalmente, é papel.
 *
 * - **Disponível:** única superfície branca pura do app (`--superficie-alta`),
 *   `--sombra-1`, serrilha no topo, carimbo DISPONÍVEL, placa pequena do código.
 * - **Retirado:** perde o branco e a sombra (`--superficie`, borda `--linha`),
 *   a placa some — não há mais nada a apresentar.
 *
 * **Nunca** prazo, contagem ou "última chance" (o voucher não expira).
 * Animação 3/3 (§4.2): quando `novo`, o carimbo é batido uma vez.
 *
 * `compacto` (auditoria de densidade P4): usado só pela Home, onde este card já
 * é precedido pelo aviso curto de "tem brinde pra retirar" — corta a instrução
 * de retirada e a nota de transferibilidade, que ficariam repetidas. A tela
 * `/vouchers` (`MeusVouchers`) é quem resgata de verdade e continua com as duas.
 */
const SERRILHA: string =
  'radial-gradient(circle 5px at 6px -1px, var(--fundo) 5px, transparent 6px)';

export function CardVoucher({
  voucher,
  novo = false,
  compacto = false,
}: {
  voucher: MeVoucher;
  novo?: boolean;
  compacto?: boolean;
}): ReactNode {
  const disponivel = voucher.status === 'disponivel';
  const boasVindas = voucher.papel === 'indicado';
  const nivelTexto = voucher.nivel != null ? `Nível ${voucher.nivel} · ` : '';

  return (
    <div>
      {novo && disponivel && <p className="mb-2 tipo-rotulo text-acento-texto">Chegou pra você.</p>}
      <article
        className={
          'relative overflow-hidden rounded-2 p-5 ' +
          (disponivel
            ? 'bg-superficie-alta shadow-[var(--sombra-1)]'
            : 'border border-linha bg-superficie')
        }
      >
        {/* serrilha — meia-lua repetida na cor do fundo, mordendo o topo */}
        <span
          aria-hidden
          className="absolute inset-x-0 top-0 h-2"
          style={{
            backgroundImage: SERRILHA,
            backgroundSize: '12px 8px',
            backgroundRepeat: 'repeat-x',
          }}
        />

        {/* carimbo */}
        <span
          className={
            'absolute right-4 top-4 rounded-1 border-[1.5px] px-1.5 py-0.5 tipo-rotulo-xs ' +
            (disponivel ? 'border-current text-acento-texto ' : 'border-current text-tinta-3 ') +
            (novo && disponivel ? 'anim-carimbo' : '')
          }
        >
          {disponivel ? 'Disponível' : 'Retirado'}
        </span>

        {disponivel ? (
          <div className="flex flex-col gap-1 pr-20">
            <p className="inline-flex items-center gap-1.5 tipo-rotulo text-tinta-2">
              <IconePresente width={16} height={16} />
              {boasVindas ? 'Brinde de boas-vindas' : 'Seu brinde'}
            </p>
            <h3 className="tipo-display text-tinta-forte">{voucher.descricao}</h3>
            <p className="tipo-apoio text-tinta-2">
              {nivelTexto}
              {voucher.origem}
            </p>

            <span className="mt-3 inline-flex w-fit items-center rounded-3 bg-grafite px-3 py-2 tipo-placa-sm text-placa-tinta shadow-[inset_0_1px_0_rgba(255,255,255,0.14)] dark:border dark:border-linha-forte">
              {voucher.voucherCodigo}
            </span>

            {!compacto && (
              <>
                <p className="mt-3 tipo-corpo text-tinta">
                  Apresente este código na Varga para retirar.
                </p>
                <p className="tipo-nota text-tinta-3">
                  Pode ser retirado por você ou por quem você quiser.
                </p>
              </>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-1 pr-20">
            <h3 className="tipo-display text-tinta-2">{voucher.descricao}</h3>
            <p className="tipo-nota text-tinta-3">
              Retirado{voucher.entregueEm ? ` em ${dataLonga(voucher.entregueEm)}` : ''}
            </p>
          </div>
        )}
      </article>
    </div>
  );
}
